import CryptoJS from "crypto-js";

/**
 * The function stores a token value in the browser's local storage.
 * @param value - The value parameter is the token that needs to be stored in the browser's local
 * storage. The function takes this value as an argument and sets it in the local storage with the key
 * 'token'.
 */
const storeToken = (value: string) => {
  localStorage.setItem("token", value);
};

/**
 * The function retrieves a token from local storage.
 * @returns The `getToken` function is returning the value of the `token` variable, which is the value
 * stored in the `localStorage` under the key `'token'`.
 */
const getToken = () => {
  const token = localStorage.getItem("token");
  return token;
};

/**
 * The function removes a specified item from the browser's local storage.
 * @param value - The parameter "value" is a string representing the name of the token to be removed
 * from the browser's local storage.
 */
const removeToken = () => {
  localStorage.removeItem("token");
};

const storeCSRF_Token = (value: string) => {
  localStorage.setItem("csrf_token", value);
};

const getCSRF_Token = () => {
  const token = localStorage.getItem("csrf_token");
  return token;
};

const removeCSRF_Token = () => {
  localStorage.removeItem("csrf_token");
};

const storeTokenExpirationInMinutes = (value: number) => {
  const expirationMilliseconds = value * 60 * 1000; // Convert minutes to milliseconds
  const expirationTime = new Date().getTime() + expirationMilliseconds;
  localStorage.setItem("tokenExpiration", expirationTime.toString());
};

const getTokenExpirationInMinutes = () => {
  const tokenExpirationTime =
    parseInt(localStorage.getItem("tokenExpiration") || "0") || 0;
  return tokenExpirationTime;
};

const removeTokenExpirationInMinutes = () => {
  localStorage.removeItem("tokenExpiration");
};

const storeExpiry = (value: string) => {
  localStorage.setItem("expiry_date", value);
};

const getExpiry = () => {
  const token = localStorage.getItem("expiry_date");
  return token;
};

const removeExpiry = () => {
  localStorage.removeItem("expiry_date");
};

// Method to securely store user data in localStorage
function setSecureLocalStorage(key: string, value: string): void {
  try {
    // Encrypt the data
    const encryptedData = CryptoJS.AES.encrypt(
      JSON.stringify(value),
      process.env.REACT_APP_SECRET_KEY as string
    ).toString();
    // Store the encrypted data in localStorage
    localStorage.setItem(key, encryptedData);
  } catch (error) {
    console.error("Error saving to localStorage:", error);
  }
}

function removeSecureLocalStorage(key?: string): void {
  localStorage.removeItem(key || '');
}

// Method to securely retrieve user data from localStorage
function getSecureLocalStorage(key: string): string | null | object {
  try {
    // Retrieve the encrypted data from localStorage
    const encryptedData = localStorage.getItem(key);
    if (encryptedData) {
      // Decrypt the data
      const bytes = CryptoJS.AES.decrypt(
        encryptedData,
        process.env.REACT_APP_SECRET_KEY as string
      );
      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
      // Parse and return the decrypted data
      return decryptedData ? JSON.parse(decryptedData) : null;
    }
    return null;
  } catch (error) {
    console.error("Error retrieving from localStorage:", error);
    return null;
  }
}


function getTypedSecureLocalStorage<T>(key: string): T | null {
  const encryptedData = localStorage.getItem(key);
  if (!encryptedData) return null;

  const bytes = CryptoJS.AES.decrypt(
    encryptedData,
    process.env.REACT_APP_SECRET_KEY as string
  );
  const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
  try {
    return JSON.parse(decryptedData) as T;
  } catch (error) {
    console.error("Error parsing local storage data", error);
    return null;
  }
}

export {
  getCSRF_Token,
  getExpiry,
  getToken,
  getTokenExpirationInMinutes,
  removeCSRF_Token,
  removeExpiry,
  removeToken,
  removeTokenExpirationInMinutes,
  storeCSRF_Token,
  storeExpiry,
  storeToken,
  storeTokenExpirationInMinutes,
  setSecureLocalStorage,
  removeSecureLocalStorage,
  getSecureLocalStorage,
  getTypedSecureLocalStorage
};
